import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import * as Types from '@xing-com/crate-common-graphql-types';
// import { useDreamTracking } from '@xing-com/crate-dream-machine-tracking';
import { Flag } from '@xing-com/flag';
import { ProfileImage } from '@xing-com/profile-image';
import { Headline, BodyCopy } from '@xing-com/typography';

import { useVomp } from '../../actions/job-card/use-vomp';
import { useDeckCardTyped, useSearchAndShuffleContext } from '../../context';

import { PeakAndPop, type PeakAndPopType } from './peak-and-pop';
import Styles from './peak-and-pop-members.style';

const getProfileImageType = (
  type?: Types.Gender | null
): 'female-user' | 'male-user' | 'neutral-user' => {
  if (type === Types.Gender.Female) {
    return 'female-user';
  }
  if (type === Types.Gender.Male) {
    return 'male-user';
  }
  return 'neutral-user';
};

const MemberProfileInfo: React.FC<{
  location?: string | null;
  jobTitle?: string | null;
  pageName?: string;
  profileImageUrl?: string;
  gender?: Types.Gender | null;
  displayName?: string;
  isPremium?: boolean;
}> = ({
  location,
  jobTitle,
  pageName,
  profileImageUrl,
  gender,
  displayName,
  isPremium,
}) => {
  const { data } = useDeckCardTyped(['TakenJob', 'AvailableJob'] as const);
  //const { trackProfileClickOnTileCommonalities } = useDreamTracking();
  const url = pageName
    ? `/profile/${pageName}?sc_o=jobs_dream_job_reco_profile_other_visit&sc_o_PropActionOrigin=jobs_dream_job_status_taken}`
    : '#';

  if (data?.type !== 'TakenJob') {
    return null;
  }

  const handleClick = (e: React.MouseEvent, url: string): void => {
    // trackProfileClickOnTileCommonalities({
    //   tileName: 'commonalities',
    //   status: data.type,
    //   itemUrn: data.urn || undefined,
    //   trackingToken: data.trackingToken || undefined,
    // });
    window.open(url, '_blank');
  };

  return (
    <Styles.ProfileInfoAnchor onClick={(e) => handleClick(e, url)}>
      <Styles.ProfileInfo
        profileImage={
          <ProfileImage
            size="medium"
            src={profileImageUrl}
            type={getProfileImageType(gender)}
          />
        }
      >
        <Headline size="medium">
          {displayName}&ensp;
          {isPremium && <Flag size="small" variant="premium" />}
        </Headline>
        {jobTitle && <BodyCopy size="small">{jobTitle}</BodyCopy>}
        {location && <BodyCopy size="small">{location}</BodyCopy>}
      </Styles.ProfileInfo>
    </Styles.ProfileInfoAnchor>
  );
};

export const PeakAndPopMembers: React.FC<PeakAndPopType> = ({ ...props }) => {
  const { data } = useDeckCardTyped(['TakenJob', 'AvailableJob'] as const);
  const { consentTaken } = useSearchAndShuffleContext();
  const { callVomp } = useVomp();

  const members = data?.similarMembers?.filter((member) => {
    return member?.user?.id;
  });

  useEffect(() => {
    if (props.isShown && members && consentTaken) {
      members.forEach((member) => {
        if (member?.user?.id) {
          callVomp(member.user.id, data.similiarMembersVompWhitelist || []);
        }
      });
    }
  }, [props.isShown]);

  if (!members) {
    return null;
  }

  return (
    <PeakAndPop {...props}>
      <Styles.PeakAndPopMembers>
        <Styles.Title size="medium">
          <FormattedMessage id="DREAM_MACHINE_JOB_CARD_OCCUPANTS_PEEK_TITLE" />
        </Styles.Title>
        <Styles.MemberContainer>
          {members.map((member) => {
            const location = member?.user?.location?.city;
            const jobTitle = member?.user?.occupations?.[0]?.subline;

            return (
              <MemberProfileInfo
                location={location}
                jobTitle={jobTitle}
                pageName={member?.user?.pageName}
                profileImageUrl={member?.user?.profileImage?.[0]?.url}
                gender={member?.user?.gender}
                displayName={member?.user?.displayName}
                key={member?.user?.id}
                isPremium={
                  member?.user?.userFlags?.displayFlag ===
                  Types.UserFlagsDisplayFlag.Premium
                }
              />
            );
          })}
        </Styles.MemberContainer>
      </Styles.PeakAndPopMembers>
    </PeakAndPop>
  );
};
